import { Link, useLocation } from "@tanstack/react-router";
import { Ellipsis } from "lucide-react";

import { cn } from "@/lib/utils";
import { getMenuList } from "@/lib/menu-list";
import { useAuth } from "@/contexts/auth-context";
import { Button } from "@/components/ui/button";
import { CollapseMenuButton } from "@/components/sidebar/collapse-menu-button";
import { FeedbackModal } from "@/components/modals/feedback-modal";
import { useState } from "react";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/tooltip";

interface MenuProps {
  isOpen: boolean | undefined;
  onItemClick?: () => void;
  onFeedbackClick?: () => void;
}

export function Menu({ isOpen, onItemClick, onFeedbackClick }: MenuProps) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const menuList = getMenuList(pathname, {
    isAdmin: !!user?.is_admin,
  });

  return (
    <div className="overflow-hidden">
      <nav className="mt-8 h-full w-full">
        <ul className="flex flex-col min-h-[calc(100vh-48px-36px-16px-32px)] lg:min-h-[calc(100vh-32px-40px-32px)] items-start space-y-1 px-2">
          {menuList.map(({ groupLabel, menus }, index) => (
            <li className={cn("w-full", groupLabel ? "pt-5" : "")} key={index}>
              {/* Sur mobile: toujours masquer les labels, sur desktop: selon isOpen */}
              {((isOpen && groupLabel) || isOpen === undefined) && (
                <p className="text-sm font-medium text-muted-foreground px-4 pb-2 max-w-[248px] truncate hidden lg:block">
                  {groupLabel}
                </p>
              )}
              {!isOpen && isOpen !== undefined && groupLabel ? (
                <TooltipProvider>
                  <Tooltip delayDuration={100}>
                    <TooltipTrigger className="w-full">
                      <div className="w-full flex justify-center items-center">
                        <Ellipsis className="h-5 w-5" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>{groupLabel}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <p className="pb-2"></p>
              )}
              {menus.map(
                ({ href, label, icon: Icon, active, submenus }, index) =>
                  !submenus || submenus.length === 0 ? (
                    <div className="w-full" key={index}>
                      <TooltipProvider disableHoverableContent>
                        <Tooltip delayDuration={100}>
                          <TooltipTrigger asChild>
                            <Button
                              variant={
                                (active === undefined &&
                                  pathname.startsWith(href)) ||
                                active
                                  ? "secondary"
                                  : "ghost"
                              }
                              className="w-full justify-start h-10 mb-1"
                              asChild={href !== "#feedback"}
                              onClick={
                                href === "#feedback"
                                  ? () => {
                                      if (onFeedbackClick) {
                                        onFeedbackClick();
                                      } else {
                                        setIsFeedbackOpen(true);
                                      }
                                      onItemClick?.();
                                    }
                                  : onItemClick
                              }
                            >
                              {href === "#feedback" ? (
                                <div className="flex items-center w-full cursor-pointer">
                                  <span
                                    className={cn(
                                      isOpen === false ? "" : "mr-4"
                                    )}
                                  >
                                    <Icon size={18} />
                                  </span>
                                  <p
                                    className={cn(
                                      "max-w-[200px] truncate transition-all",
                                      isOpen === false && "-translate-x-96 opacity-0",
                                      isOpen !== false && "translate-x-0 opacity-100"
                                    )}
                                  >
                                    {label}
                                  </p>
                                </div>
                              ) : (
                                <Link to={href} onClick={onItemClick}>
                                  <span
                                    className={cn(
                                      // Add margin when open
                                      isOpen === false ? "" : "mr-4"
                                    )}
                                  >
                                    <Icon size={18} />
                                  </span>
                                  <p
                                    className={cn(
                                      "max-w-[200px] truncate transition-all",
                                      // Hide when closed, show when open
                                      isOpen === false && "-translate-x-96 opacity-0",
                                      isOpen !== false && "translate-x-0 opacity-100"
                                    )}
                                  >
                                    {label}
                                  </p>
                                </Link>
                              )}
                            </Button>
                          </TooltipTrigger>
                          {isOpen === false && (
                            <TooltipContent side="right">
                              {label}
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  ) : (
                    <div className="w-full" key={index}>
                      <CollapseMenuButton
                        icon={Icon}
                        label={label}
                        submenus={submenus}
                        isOpen={isOpen}
                        onItemClick={onItemClick}
                      />
                    </div>
                  )
              )}
            </li>
          ))}
        </ul>
      </nav>
      {!onFeedbackClick && (
        <FeedbackModal open={isFeedbackOpen} onOpenChange={setIsFeedbackOpen} />
      )}
    </div>
  );
}
